import { useEffect, useMemo, useState } from "react";

import useSingleLead from "../../../data/useSingleLead";
import { ILead, QuoteType } from "../../../models/Lead";
import Card from "../../shared/Card/Card";
import LoadingWheel from "../../shared/LoadingWheel";
import BookingQuoteCalculationTabs from "./QuoteCalculation/BookingQuoteCalculationTabs";
import QuoteCalculationBreakdown from "./QuoteCalculation/QuoteCalculationBreakdown";
import {
  buildRowsForSide,
  getQuoteTypeLabel,
  parseCalculationData,
} from "./QuoteCalculation/quoteCalculationUtils";
import useSav from "../../../data/useSav";
import { useAuth } from "../../../contexts/AuthContext";
import { wilmaSAVCustomerId } from "../../../config";

export const GetQuoteType = (quoteType: QuoteType) => getQuoteTypeLabel(quoteType);

export default function QuoteCalculationCard({
  leadId,
  quoteType,
  isCollection,
  total,
  isBooking = false,
}: {
  leadId: string;
  quoteType: QuoteType;
  isCollection: boolean;
  total: number;
  isBooking?: boolean;
}) {
  const { lead: singleLead } = useSingleLead(leadId, "QuoteCalculationCard");
  const [lead, setLead] = useState<ILead | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [calculationData, setCalculationData] = useState<ReturnType<typeof parseCalculationData> | null>(null);
  const { customerId } = useAuth();
  const isSavCustomer = !!customerId && customerId === wilmaSAVCustomerId;
  const { customerNames } = useSav(
    isSavCustomer ? customerId : undefined,
    !isSavCustomer,
  );
  const [dropOffCustomerName, setDropOffCustomerName] = useState<string | undefined>();
  const [collectionCustomerName, setCollectionCustomerName] = useState<string | undefined>();
  const [dropOffCustomerColour, setDropOffCustomerColour] = useState<string | undefined>();
  const [collectionCustomerColour, setCollectionCustomerColour] = useState<string | undefined>();

  useEffect(() => {
    if (!singleLead.isLoading && singleLead.data && ( !isSavCustomer || !customerNames.isLoading )) {
      const leadData = singleLead.data.data;
      if (customerNames.data) {
        const dropOffCustomer = customerNames.data.data?.find(x => x.id === leadData.dropOffCustomerId);
        const collectionCustomer = customerNames.data.data?.find(x => x.id === leadData.collectionCustomerId);
        setDropOffCustomerName(dropOffCustomer?.name);
        setCollectionCustomerName(collectionCustomer?.name);
        setDropOffCustomerColour(dropOffCustomer?.primaryColour);
        setCollectionCustomerColour(collectionCustomer?.primaryColour);
      }
      setLead(leadData);
      setCalculationData(parseCalculationData(leadData.calculationJson));
      setIsLoading(false);
    }
  }, [singleLead.isLoading, singleLead.data, customerNames.isLoading, customerNames.data]);

  const activeQuoteType = lead?.quoteType ?? quoteType;
  const activeIsCollection = lead?.isCollection ?? isCollection;

  const activeRows = useMemo(() => {
    if (!calculationData) return [];
    return buildRowsForSide(calculationData, activeQuoteType, activeIsCollection);
  }, [calculationData, activeQuoteType, activeIsCollection]);

  if (isLoading || lead === undefined) {
    return (
      <Card bodyClassName="p-4" title="Quote Calculation">
        <LoadingWheel />
      </Card>
    );
  }

  if (!calculationData) {
    return (
      <Card bodyClassName="p-4" title="Quote Calculation">
        <p className="text-sm text-gray-500">No calculation details available.</p>
      </Card>
    );
  }

  if (isBooking) {
    return (
      <Card bodyClassName="p-4" title="Quote Calculations" collapsible>
        <BookingQuoteCalculationTabs
          calculationData={calculationData}
          actualQuoteType={activeQuoteType}
          actualIsCollection={activeIsCollection}
          actualTotal={total}
          collectionCustomerName={collectionCustomerName}
          collectionCustomerColour={collectionCustomerColour}
          dropOffCustomerName={dropOffCustomerName}
          dropOffCustomerColour={dropOffCustomerColour}
        />
      </Card>
    );
  }

  return (
    <Card
      bodyClassName="p-4 divide-y divide-gray-200"
      collapsible
      title={`${GetQuoteType(activeQuoteType)} ${activeIsCollection ? "Collection" : "Drop Off"} Calculation`}
    >
      <QuoteCalculationBreakdown
        calculations={activeRows}
        isCollection={activeIsCollection}
        dropOff={calculationData.dropOff}
        total={total}
      />
    </Card>
  );
}
